import type { IFilterQuery } from '@shared';
import {
  type IUpdateAdPlaceTape,
  type AdsStatus,
  type ICreateParentAds,
  type ICustomerAdCount,
  type IFilterFavoriteQuery,
  type IParentAds,
  type IParentAdsPageableResponseData,
  type IUpdateParentAds,
  type IUpdateParentAdsStatus,
} from '@shared/ads-modules';

const ENDPOINT_PREFIX = 'parent-ads';

export const useParentAdsStore = defineStore('parent-ads', () => {
  const apiClient = useApiClient();
  const parentAds = ref<IParentAds | null>(null);
  const parentAdsList = ref<IParentAds[]>([]);
  const customerAdsList = ref<IParentAds[]>([]);
  const count = ref<ICustomerAdCount[]>([]);
  const upAds = ref();

  const getById = async (id: string) => {
    const result = await apiClient.get<IParentAds>(ENDPOINT_PREFIX + '/' + id);
    if (result.success) parentAds.value = result.data as IParentAds;
    return result;
  };

  const create = async (payload: ICreateParentAds) => {
    return await apiClient.postFormData<ICreateParentAds, IParentAds>(
      ENDPOINT_PREFIX,
      payload
    );
  };

  const update = async (payload: IUpdateParentAds) => {
    return await apiClient.putFormData<IUpdateParentAds, IParentAds>(
      ENDPOINT_PREFIX,
      payload
    );
  };

  const filter = async (payload?: IFilterQuery, saveOld = false) => {
    if (!payload) payload = { page: 1, limit: 1000 };
    const result = await apiClient.post<
      IFilterQuery,
      IParentAdsPageableResponseData
    >(ENDPOINT_PREFIX + '/filter', payload);
    if (result.success) {
      const data = result.data?.data as IParentAds[];
      parentAdsList.value = saveOld ? [...parentAdsList.value, ...data] : data;
    }
    return result;
  };

  const customerFilter = async (payload?: IFilterQuery, saveOld = false) => {
    if (!payload) payload = { page: 1, limit: 1000 };
    const result = await apiClient.post<
      IFilterQuery,
      IParentAdsPageableResponseData
    >(ENDPOINT_PREFIX + '/customer/filter', payload);
    if (result.success) {
      const data = result.data?.data as IParentAds[];
      customerAdsList.value = saveOld
        ? [...customerAdsList.value, ...data]
        : data;
    }
    return result;
  };

  const customerFavorite = async (payload: IFilterFavoriteQuery) => {
    return await apiClient.post<IFilterFavoriteQuery, IParentAds[]>(
      ENDPOINT_PREFIX + '/customer/favorite',
      payload
    );
  };

  const deleteById = async (id: string) => {
    return await apiClient.delete(ENDPOINT_PREFIX + '/' + id);
  };

  const updateStatus = async (payload: IUpdateParentAdsStatus) => {
    return await apiClient.put<IUpdateParentAdsStatus, IParentAds>(
      ENDPOINT_PREFIX + '/customer/status',
      payload
    );
  };

  const getTotalCount = async (payload: IFilterQuery) => {
    return await apiClient.post<IFilterQuery, number>(
      ENDPOINT_PREFIX + '/total-count',
      payload
    );
  };

  const getCountOfAds = async () => {
    const result = await apiClient.get(ENDPOINT_PREFIX + '/customer/ads/count');
    if (result.success) {
      count.value = result.data as ICustomerAdCount[];
    }
    return result;
  };

  const getUpAds = async (id: string) => {
    const result = await apiClient.put<IUpdateAdPlaceTape, IParentAds>(
      ENDPOINT_PREFIX + '/customer/up-ads',
      { id }
    );
    if (result.success) upAds.value = result.data;
    return result;
  };

  const sendViewNotUniq = async (id: string) => {
    return await apiClient.put(`${ENDPOINT_PREFIX}/set-not-uniq-views/${id}`);
  };

  const sendViewUniq = async (id: string) => {
    return await apiClient.put(`${ENDPOINT_PREFIX}/set-uniq-views/${id}`);
  };

  return {
    getById,
    create,
    update,
    filter,
    customerFilter,
    customerFavorite,
    parentAds,
    parentAdsList,
    customerAdsList,
    count,
    deleteById,
    updateStatus,
    getTotalCount,
    getCountOfAds,
    getUpAds,
    upAds,
    sendViewNotUniq,
    sendViewUniq,
  };
});
